import { useMemo, useState } from "react";
import { List, Map as MapIcon, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi } from "@/hooks/use-api";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { PlantMap } from "@/components/map/PlantMap";
import { PlantTable, sortDeviationsFirst } from "@/components/plants/PlantTable";
import { fmtNum } from "@/lib/format";

/* Kraftverk: a register of comparable data. Search and status filters above, the same table as on the overview
   (with municipality), or the map. No outer frame around the table. */
const STATUS_FILTERS = [
  { key: "running", label: "I drift" },
  { key: "reduced", label: "Redusert" },
  { key: "stopped", label: "Stanset" },
  { key: "maintenance", label: "Vedlikehold" },
];

export default function PlantsPage() {
  const [q, setQ] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [view, setView] = useState("grid");
  const { data, isLoading, isError, refetch } = useApi("/plants", {}, { refetchInterval: 120_000 });
  const plants = useMemo(() => {
    let list = data?.plants || [];
    if (statuses.length) list = list.filter((p) => statuses.includes(p.status));
    if (q.trim()) {
      const ql = q.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(ql) || (p.municipality || "").toLowerCase().includes(ql));
    }
    return sortDeviationsFirst(list);
  }, [data, statuses, q]);
  const counts = useMemo(() => (data?.plants || []).reduce((acc, p) => ({ ...acc, [p.status]: (acc[p.status] || 0) + 1 }), {}), [data]);
  const totals = useMemo(() => {
    const all = data?.plants || [];
    return { power: all.reduce((s, p) => s + (p.power_mw || 0), 0), cap: all.reduce((s, p) => s + (p.capacity_mw || 0), 0) };
  }, [data]);

  if (isError) return <ErrorState message="Kunne ikke hente kraftverk." onRetry={refetch} />;

  return (
    <div className="space-y-6" data-testid="plants-page">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2" data-testid="plants-header">
        <div className="min-w-0">
          <h1 className="text-[28px] font-semibold leading-[1.15] tracking-[-0.015em]">Kraftverk</h1>
          {isLoading ? <Skeleton className="mt-2 h-5 w-72" /> : (
            <p className="mt-1 text-[15px] text-muted-foreground" data-testid="plants-meta">
              <span className="metric">{data.plants.length}</span> kraftverk · <span className="metric">{fmtNum(totals.power, 1)}</span> av <span className="metric">{fmtNum(totals.cap, 1)}</span> MW produseres nå
            </p>
          )}
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="segmented">
            <TabsTrigger value="grid" className="gap-1.5 px-2.5" aria-label="Listevisning" data-testid="plants-view-grid">
              <List className="h-3.5 w-3.5" /> Liste
            </TabsTrigger>
            <TabsTrigger value="map" className="gap-1.5 px-2.5" aria-label="Kartvisning" data-testid="plants-view-map">
              <MapIcon className="h-3.5 w-3.5" /> Kart
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-4">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk kraftverk eller kommune" className="h-9 pl-8 text-[14px]" data-testid="plants-search-input" />
        </div>
        <ToggleGroup type="multiple" value={statuses} onValueChange={setStatuses} className="flex-wrap justify-start gap-0.5">
          {STATUS_FILTERS.map((s) => (
            <ToggleGroupItem key={s.key} value={s.key} size="sm" className="chip" data-testid={`plants-filter-${s.key}`}>
              {s.label}
              <span className="metric text-muted-foreground/70">{counts[s.key] || 0}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {view === "map" ? (
        <div className="overflow-hidden rounded-lg border border-border">
          <PlantMap plants={plants} height={600} />
        </div>
      ) : isLoading || plants.length ? (
        <PlantTable plants={plants} loading={isLoading} showMunicipality testId="plants-table" />
      ) : (
        <EmptyState title="Ingen kraftverk matcher filteret" description="Nullstill søk eller statusfilter." action="Nullstill" onAction={() => { setQ(""); setStatuses([]); }} testId="plants-empty" />
      )}
    </div>
  );
}
